<script setup lang="ts">
import { capitalize, type Component, computed } from "vue";
import { useRouter } from "vue-router";

import actions from "~/actions";
import Avatar from "~/components/dumb/Avatar.vue";
import EntityPreviewTooltip from "~/components/dumb/EntityPreviewTooltip.vue";
import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import PageIcon from "~/components/dumb/PageIcon.vue";
import StatusIcon from "~/components/dumb/StatusIcon.vue";
import PageLink from "~/components/text/plugins/PageLink.vue";
import { COMPLETED_STATUS_KINDS, DEACTIVATED_USER_LABEL } from "~/components/visualization/constants";
import {
  AutomationsIcon,
  CalendarIcon,
  ChatIcon,
  CommentIcon,
  DartIcon,
  FileIcon,
  FormIcon,
  GithubIcon,
  MailIcon,
  PropertiesIcon,
  RecurrenceIcon,
  ReminderIcon,
  SkillIcon,
  SlackIcon,
  SprintIcon,
} from "~/icons";
import { EntityName, EventActor, IconSize, PageLinkMode } from "~/shared/enums";
import type { Page } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useTenantStore } from "~/stores";
import { asyncState } from "~/utils/async";
import { getDocLink, getPageLink } from "~/utils/common";
import { useAsyncDoc, useAsyncTask } from "~/utils/entityIncrementalLoading";
import { getTitleOrNoTitle } from "~/utils/entityTitle";
import { getUserDisplayName } from "~/utils/user";
import { hasCustomAutomationIcon } from "~/views/settings/AutomationsView/common";

const ENTITY_NAMES_WITH_PAGE_ICON = new Set([
  EntityName.DARTBOARD,
  EntityName.VIEW,
  EntityName.SPACE,
  EntityName.FOLDER,
  EntityName.FORM,
]);

const EVENT_ACTORS = new Set<string>(Object.values(EventActor));
const EVENT_ACTOR_TO_ICON = new Map([
  [EventActor.DART_AUTOMATION, AutomationsIcon],
  [EventActor.DART_DUE_DATE_BOT, CalendarIcon],
  [EventActor.DART_GITHUB_BOT, GithubIcon],
  [EventActor.DART_RECURRING_TASK_BOT, RecurrenceIcon],
  [EventActor.DART_REMINDER_BOT, ReminderIcon],
  [EventActor.DART_SLACK_BOT, SlackIcon],
  [EventActor.DART_SPRINT_BOT, SprintIcon],
  [EventActor.FORM_USER, FormIcon],
  [EventActor.EMAIL_USER, MailIcon],
]);
const EVENT_NAME_TO_ICON = new Map<string, Component>([
  [EntityName.ATTACHMENT, FileIcon],
  [EntityName.CHAT, ChatIcon],
  [EntityName.COMMENT, CommentIcon],
  [EntityName.PROPERTY, PropertiesIcon],
  [EntityName.SKILL, SkillIcon],
  [EntityName.STATUS, StatusIcon],
  [EntityName.TENANT, Avatar],
]);

const props = defineProps<{
  name: EntityName;
  link?: string;
  duid?: string;
  alt?: string;
  automationDuid?: string;
  labelOverride?: string;
  mode?: PageLinkMode;
  shrink?: boolean;
  muted?: boolean;
  notInText?: boolean;
}>();

const emit = defineEmits<{
  open: [];
  remove: [];
  click: [event: MouseEvent];
  keydown: [event: KeyboardEvent];
}>();

const router = useRouter();
const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();

const { state: entity, isLoading: isLoadingEntity } = asyncState(
  async () => (props.duid ? dataStore.agetEntity(props.duid, props.name) : undefined),
  {
    default: props.duid ? dataStore.getEntity(props.duid, props.name) : undefined,
    watch: () => (props.duid ? dataStore.agetEntity(props.duid, props.name) : undefined),
  }
);

const { task, isLoading: isLoadingTask } = useAsyncTask(
  computed(() => (props.name === EntityName.TASK && props.duid ? props.duid : undefined))
);

const isCompletedTask = computed(() => {
  if (!task.value) {
    return undefined;
  }
  const statusKind = dataStore.getStatusByDuid(task.value.statusDuid)?.kind;
  if (!statusKind) {
    return undefined;
  }
  return COMPLETED_STATUS_KINDS.has(statusKind);
});

const { doc, isLoading: isLoadingDoc } = useAsyncDoc(
  computed(() => (props.name === EntityName.DOC && props.duid ? props.duid : undefined))
);

const skill = computed(() =>
  props.name === EntityName.SKILL && props.duid ? dataStore.getSkillByDuid(props.duid) : undefined
);
const isSkillEntity = computed(() => props.name === EntityName.SKILL);

const user = computed(() => {
  if (props.name !== EntityName.USER || !props.duid) {
    return undefined;
  }

  return dataStore.getUserByDuid(props.duid);
});
const automation = computed(() =>
  props.name === EntityName.USER && props.duid === EventActor.DART_AUTOMATION && props.automationDuid
    ? dataStore.getAutomationByDuid(props.automationDuid)
    : undefined
);

const otherEntityTitle = computed(
  () =>
    entity.value?.title ??
    (props.name === EntityName.USER && props.duid && EVENT_ACTORS.has(props.duid)
      ? (props.alt ?? props.duid)
      : props.name !== EntityName.UNKNOWN
        ? (props.alt ?? capitalize(props.name))
        : "Dart")
);

const space = computed(() => {
  if (!entity.value || (props.name !== EntityName.DARTBOARD && props.name !== EntityName.FOLDER)) {
    return undefined;
  }

  const fullEntity = (props.name === EntityName.DARTBOARD ? dataStore.getDartboardByDuid : dataStore.getFolderByDuid)(
    entity.value.duid
  );
  if (!fullEntity) {
    return undefined;
  }
  return dataStore.getSpaceByDuid(fullEntity.spaceDuid);
});
const pageEntityTitle = computed(() => {
  if (!entity.value) {
    return "";
  }
  const title = getTitleOrNoTitle(entity.value.title);
  return space.value ? `${space.value.abrev}/${title}` : title;
});

const otherEntityIcon = computed(() => {
  if (automation.value && hasCustomAutomationIcon(automation.value)) {
    return PageIcon;
  }
  if (props.name === EntityName.USER) {
    return EVENT_ACTOR_TO_ICON.get(props.duid as EventActor) ?? DartIcon;
  }
  return EVENT_NAME_TO_ICON.get(props.name) ?? DartIcon;
});
const otherEntityIconArgs = computed(() => {
  if (automation.value && hasCustomAutomationIcon(automation.value)) {
    return { page: automation.value, size: IconSize.S };
  }
  if (props.name === EntityName.PROPERTY || props.name === EntityName.STATUS) {
    return { duid: props.duid };
  }
  if (props.name === EntityName.TENANT && entity.value) {
    return {
      abrev: tenantStore.name[0] ?? "D",
      imageUrl: tenantStore.imageUrl,
      class: "icon-sm",
    };
  }
  return {};
});

const isFormerUser = computed(
  () =>
    props.name === EntityName.USER && !user.value && !isLoadingEntity.value && !EVENT_ACTORS.has(props.duid as string)
);

const isLoading = computed(() => isLoadingEntity.value || isLoadingDoc.value || isLoadingTask.value);

const isNotificationMode = computed(() => props.mode === PageLinkMode.NOTIFICATION);

const wrapperClass = computed(() => (props.shrink ? "!inline-flex truncate align-middle" : "!inline align-baseline"));

const clickTask = (e: MouseEvent) => {
  emit("click", e);
  if (isNotificationMode.value) {
    emit("open");
  }

  if (!task.value) {
    return;
  }

  actions.visualization.navigateToTask(task.value.duid);
};

const clickOther = async (e: MouseEvent) => {
  emit("click", e);
  if (isNotificationMode.value && props.name === EntityName.COMMENT) {
    emit("open");
  }

  if (props.name === EntityName.ATTACHMENT) {
    if (!props.duid) {
      return;
    }
    const attachment = await dataStore.getAttachmentByDuid(props.duid);
    if (!attachment) {
      return;
    }
    appStore.setFullscreenMediaModalOpen({
      attachmentFileUrl: attachment.fileUrl,
      attachmentName: attachment.name,
      kind: attachment.kind,
      entityName: "",
    });
    return;
  }

  if (props.name === EntityName.SKILL && skill.value) {
    const route = router.currentRoute.value;
    router.push({ path: route.path, query: { ...route.query, settings: "skills", skill: skill.value.duid } });
    return;
  }

  if (props.name === EntityName.USER && props.duid === EventActor.DART_AUTOMATION) {
    const route = router.currentRoute.value;
    router.push({
      path: route.path,
      query: { ...route.query, settings: "automations", automation: props.automationDuid },
    });
    return;
  }

  if (props.link) {
    router.push(new URL(props.link).pathname);
    return;
  }
  if (!entity.value) {
    return;
  }

  const link = await dataStore.getEntityLink(entity.value, props.name);
  if (link) {
    router.push(link);
  }
};

const onDocLinkClick = (e: MouseEvent) => {
  if (!doc.value) {
    return;
  }
  emit("click", e);
  router.push(getDocLink(doc.value));
};

const onPageLinkClick = (e: MouseEvent) => {
  emit("click", e);
  router.push(getPageLink(entity.value as Page, dataStore.getSpaceByDuid));
};

const onContextMenu = (event: MouseEvent) => {
  if (!doc.value || (tenantStore.isDart && !pageStore.adminHidden && event.altKey)) {
    return;
  }

  appStore.openContextMenu(event as PointerEvent, actions.context.doc(doc.value));
};
</script>

<template>
  <div v-if="isLoading" class="inline-flex min-h-6 w-64 rounded-sm bg-opposite/5 align-middle">
    <LoadingSpinner x-small />
  </div>

  <EntityPreviewTooltip v-else-if="task" :task-duid="task.duid" class="max-w-full" :class="wrapperClass">
    <PageLink
      :title="task.title"
      :icon="!isNotificationMode ? StatusIcon : undefined"
      :icon-args="!isNotificationMode ? { duid: task.statusDuid } : undefined"
      :strikethrough="isCompletedTask"
      :mode="mode"
      :muted="muted"
      :not-in-text="notInText"
      :shrink="shrink"
      @remove="emit('remove')"
      @click="clickTask"
      @keydown="emit('keydown', $event)" />
  </EntityPreviewTooltip>

  <EntityPreviewTooltip v-else-if="doc" :doc-duid="doc.duid" class="max-w-full" :class="wrapperClass">
    <PageLink
      :mode="mode"
      :page="doc"
      :title="doc.title"
      :muted="muted"
      :not-in-text="notInText"
      :shrink="shrink"
      @remove="emit('remove')"
      @context-menu="onContextMenu"
      @click="onDocLinkClick"
      @keydown="emit('keydown', $event)" />
  </EntityPreviewTooltip>

  <EntityPreviewTooltip v-else-if="user" :user-duid="user.duid" :class="wrapperClass">
    <PageLink
      :title="labelOverride ?? getUserDisplayName(user)"
      :icon="Avatar"
      :icon-args="{
        user,
        class: 'icon-sm',
      }"
      :mode="mode"
      :muted="muted"
      :not-in-text="notInText"
      :shrink="shrink"
      @remove="emit('remove')"
      @click="clickOther"
      @keydown="emit('keydown', $event)" />
  </EntityPreviewTooltip>

  <EntityPreviewTooltip
    v-else-if="isSkillEntity && entity"
    :skill-duid="entity.duid"
    class="max-w-full"
    :class="wrapperClass">
    <PageLink
      :title="entity.title"
      :page="skill"
      :icon="skill ?? SkillIcon"
      :mode="mode"
      :muted="muted"
      :not-in-text="notInText"
      :shrink="shrink"
      @remove="emit('remove')"
      @click="clickOther"
      @keydown="emit('keydown', $event)" />
  </EntityPreviewTooltip>

  <PageLink
    v-else-if="ENTITY_NAMES_WITH_PAGE_ICON.has(name) && entity"
    :title="pageEntityTitle"
    :page="entity as Page"
    :mode="mode"
    :muted="muted"
    :not-in-text="notInText"
    :shrink="shrink"
    :class="shrink && 'align-middle'"
    @remove="emit('remove')"
    @click="onPageLinkClick"
    @keydown="emit('keydown', $event)" />

  <span v-else-if="isFormerUser" class="text-md">{{ DEACTIVATED_USER_LABEL }}</span>

  <PageLink
    v-else
    :title="otherEntityTitle"
    :icon="otherEntityIcon"
    :icon-args="otherEntityIconArgs"
    :mode="mode"
    :muted="muted"
    :not-in-text="notInText"
    :shrink="shrink"
    :class="shrink && 'align-middle'"
    @remove="emit('remove')"
    @click="clickOther"
    @keydown="emit('keydown', $event)" />
</template>
